// ** Next imports
import Link from "next/link";

// ** File imports
import Layout, { SEOHead } from "../../layouts/default";
import { FaqSidebar } from "../../components/support/faqPages";

interface PropsEl {
  children: React.ReactNode;
}

const Pcol = (props: PropsEl) => {
  return <p className="col-grey1">{props.children}</p>;
};

const Hcol = (props: PropsEl) => {
  return <h4 className="col-black">{props.children}</h4>;
};

const BroadbandInformation = () => (
  <Layout>
    <SEOHead title="Broadband Information" />
    <section className="bg-silver1 pad-top-40 pad-bot-40">
      <div className="container">
        <div className="sec-head">
          <h1 className="no-margin text-center">
            <b>Broadband Information</b>
          </h1>
        </div>
      </div>
    </section>

    <section className="pad-top-60 pad-bot-60">
      <div className="container">
        <div className="row">
          <div className="col-md-9 col-lg-9 col-sm-9 col-12">
            <div className="faq-textual">
              <Pcol>
                At EIN, we want our customers to have information to more fully
                understand and enjoy the services we offer. To help keep
                customers informed about our mass market broadband Internet
                access services, the EIN website (
                <Link href="/">easyinternetnow.com</Link>) describes the mass
                market wireless and wired broadband Internet access services we
                offer. In this document, we provide information about the
                network practices, performance characteristics, and commercial
                terms applicable to our mass market wired connections consistent
                with the Federal Communications Commission’s Open Internet
                Rules. This information should help customers make informed
                choices about how to use those services, and will assist
                providers of Internet applications, content and services in
                developing, marketing and maintaining their Internet offerings.
                We encourage mass market customers and other users of our
                network to familiarize themselves with this information, and to
                provide EIN with feedback about our mass market broadband
                Internet access services so that we can continue to provide an
                excellent experience.
              </Pcol>

              <Pcol>
                Nothing in this document changes your rights and obligations, or
                ours, under our terms of service associated with the applicable
                products, Acceptable Use Policy (“AUP”) or Privacy Policy. This
                document and the information contained in it are provided for
                informational purposes only and may be changed at any time,
                without notice.
              </Pcol>

              <Hcol>Network Practices</Hcol>
              <Pcol>
                EIN strives to provide a high-quality Internet experience for
                all of our customers. Because the Internet consists of multiple
                interconnected networks and most Internet end points (e.g.,
                websites and other content providers) are not directly connected
                to the EIN network, EIN must connect to and exchange traffic
                with other networks to provide its subscribers the capability of
                uploading data to or downloading data from Internet end points
                that are connected to those networks. To that end, EIN has
                entered into commercially negotiated agreements to exchange
                traffic with those networks (and the networks with which those
                networks are connected) on mutually agreeable terms. The links
                EIN and other networks use to exchange such traffic may become
                congested at times. Consistent with its agreements with those
                other networks and its long-standing practice, EIN may establish
                or expand the connections between its network and other
                networks, but only on mutually agreeable terms. If EIN is unable
                to reach agreement on terms of interconnection or network
                expansion with these other networks, it could affect customers’
                ability to upload or download data to Internet endpoints
                connected to those networks. EIN does not guarantee that it will
                establish or expand the connections between its network and
                other networks, or that subscribers will be able to upload data
                to or download data from Internet end points connected to other
                networks at any particular speed.
              </Pcol>
              <Pcol>
                In addition, like the other networks that make up the Internet,
                the EIN network is a shared network, which means that the
                transmission links and other network resources used to provide
                broadband services are shared among EIN’s subscribers. EIN
                manages this network for the benefit of all users based on a
                variety of factors, and our technical expertise. Nonetheless,
                temporary congestion may occur when a large number of customers
                in a concentrated area access the network at the same time or
                when some customers consume a very large amount of network
                capacity during busy periods, such as at stadium events, during
                peak usage times, or during planned network maintenance.
              </Pcol>
              <Pcol>
                As is common in the industry, we use network management
                practices and other tools to manage network resources for the
                benefit of all of our broadband customers, especially during
                periods when network demand exceeds available network resources
                (also known as “congestion”). As you would expect, our network
                management practices and our service offerings have evolved over
                time to benefit our customers and take advantage of the billions
                we have spent to expand and augment our networks. EIN does not
                favor certain Internet applications by blocking, throttling or
                modifying particular protocols, protocol ports, or protocol
                fields in ways not prescribed by the protocol standards.
                However, in response to a specific security threat against our
                network or our customers, EIN may occasionally need to limit the
                flow of traffic from certain locations or take other appropriate
                actions. In addition, we prevent the use of certain ports on our
                wired Internet access services to help protect our customers and
                network against malicious activity.
              </Pcol>
              <Pcol>
                The EIN Copyright Alert Program was established to respond to
                alleged copyright infringement activities using peer-to-peer
                file sharing, and attempts to educate customers about the
                importance of protecting copyright and lawful use of content
                available over the Internet. Under the program, content owners
                may notify EIN of alleged copyright infringement based on the IP
                address of a user. EIN then will attempt to identify a
                subscriber account based on that IP address and forward a
                copyright alert to the subscriber account, advising the account
                holder of the allegation and providing information about online
                copyright infringement. If a subscriber receives additional
                alerts, we may temporarily redirect the account holder’s
                broadband Internet access service to a webpage where the account
                holder must review material on the importance of copyright and
                the lawful use of content available over the Internet. Upon
                completion of this review, such redirection will be discontinued
                and the subscriber’s service will be restored to normal. After
                this stage, if a subscriber continues to receive additional
                alerts, then EIN may take action consistent with Section 512(i)
                of the Digital Millennium Copyright Act, which may result in
                termination of the subscriber/accountholder’s broadband Internet
                access service. Account holders’ personally identifiable
                information is protected throughout this process —EIN will not
                provide such information to content owners unless required to do
                so by court order.
              </Pcol>
              <Pcol>
                EIN takes the security of our customers and our network very
                seriously. We proactively monitor network activity to help guard
                against a wide range of security threats, including viruses,
                botnets, worms, distributed denial of service attacks, SPAM, and
                other harmful activity. We encourage customers to adopt their
                own security practices.
              </Pcol>
              <Pcol>
                We use a variety of network tools to monitor network activity
                and health to maintain its stability and functionality, to
                protect the network against threats, and for other operational
                purposes. We store the information we gather through this
                monitoring for only as long as we have a business purpose to
                maintain it. The EIN Privacy Policy describes how we collect,
                use and share this information. You can view EIN’s Privacy
                Policy at:{" "}
                <Link href="/privacy-policy">
                  https://easyinternetnow.com/privacy-policy
                </Link>
                .
              </Pcol>
              <Pcol>
                If we detect a security threat, we will typically attempt to
                isolate the threat and minimize the impact to network service.
                We may use a variety of security measures to protect the
                network, including blocking malicious or unlawful traffic,
                redirecting the flow of traffic over some portions of our
                network, or taking other actions to address the threat. For
                example, as described in more detail below, we block certain
                ports that transfer malicious or disruptive traffic (such as
                Ports 25, 135, 139, 445, and 1900). We attempt to limit actions
                to the specific portions of our network or customer base
                impacted by the security threat and only for as long as
                necessary to mitigate the threat.
              </Pcol>
              <Pcol>
                EIN may scan or analyze network addresses that are registered
                through EIN, including addresses that may have been delegated to
                customers, and/or routes that originate from EIN-provided
                networks to detect vulnerabilities that might be used to
                compromise EIN or customer assets or might be used in attacks
                against others. In doing so, we seek to avoid disrupting network
                service to customers. We may use information derived from these
                activities to identify and address security issues or to notify
                customers of issues.
              </Pcol>
              <Pcol>
                As noted above, EIN blocks certain ports that transfer malicious
                or disruptive traffic to protect our customers and our network.
                Below is more information about port blocking that is currently
                in place. We may block additional ports in the future based upon
                threat assessments.
              </Pcol>
              <Pcol>
                Port 0/TCP: Port 0 is a reserved port. This port should not be
                used for any applications. Blocking protects our customers from
                potentially harmful types of network abuses. Port 19/UDP: Port
                19 Chargen is a protocol designed to generate a stream of
                characters for debugging and measurement. Because more recent
                tools have been developed for measurement and debugging
                purposes, blocking protects against use of this port in
                Reflective DDOS attacks. Port 25/TCP: Simple Mail Transport
                Protocol (SMTP) is used to send email. Port 25/TCP may be
                blocked from customers with dynamically-assigned Internet
                Protocol (IP) addresses to protect systems from becoming a mail
                relay for SPAM. Customers can subscribe to EIN SMTP services if
                they need to host an SMTP server on the Internet. Port 68/UDP:
                Port 68 is used to obtain dynamic IP address information from a
                dynamic host configuration protocol (DHCP) server. Port 68 may
                be blocked to eliminate the risk of exposure to a rogue DHCP
                server. Port 123/UDP: Network Time Protocol (NTP) is used to
                accurately synchronize computer time of day to a reference time
                server. Some aspects of Port 123 may be limited to minimize
                malicious use. Poorly-configured NTP servers can be used for
                Reflective DDOS attacks, and some devices provide NTP service
                inadvertently, which exacerbates the port’s malicious use. Port
                135/TCP: NetBIOS is a network file sharing protocol and is also
                known as Common Internet File System or LanManager. Blocking
                protects customers from exposing files unintentionally, worms,
                and viruses. Port 139/TCP: NetBIOS is a network file sharing
                protocol and is also known as Common Internet File System or
                LanManager. Blocking protects customers from exposing critical
                system files unintentionally, which could give system access to
                a malicious actor. Port 445/TCP: NetBIOS is a network file
                sharing protocol and is also known as Common Internet File
                System or LanManager. Blocking mitigates a potential threat to
                certain operating systems. Similar to our blocking of Ports 135
                and 139, blocking Port 445 protects customers from exposing
                files unintentionally, worms, and viruses. Port 520/UDP: RIPv1 –
                UDP port 520 is used by the Routing Information Protocol (RIP)
                to share network routing information. RIPv1 was designed to
                support route information sharing on small classful (class A, B,
                C, D) networks and has limited usefulness in today’s classless
                networks. Port 520 has been used by malicious actors to generate
                Reflective DDOS attacks. Port 1900/UDP: Universal Plug and Play
                (UPnP) is a protocol standard designed to allow device discovery
                over a local network. Some home routers may expose this port to
                the Internet, which could allow attackers to defeat the security
                attributes of Network Address Translation (NAT) and allow
                attackers to use the port for Reflective DDOS attacks. Does EIN
                restrict the types of devices that customers can use with its
                mass market broadband Internet access services? EIN customers
                may use devices of their choice (PC, Smartphones, Tablets, Smart
                TV, etc.) to connect to our wired broadband Internet access
                service via the wiring at their home or business premises, or
                via Wi-Fi connected to their EIN wired broadband Internet access
                service (connection options vary based on device capabilities).
                EIN generally does not support IEEE2 802.11b or earlier Wi-Fi
                protocols. Devices must also be used in a manner consistent with
                our terms of service and Acceptable Use Policy. For example,
                some data plans are designated for use with only a basic phone
                or smartphone, in which case customers may not use their device
                to provide an Internet Access connection to other
                equipment/devices (such as computers, netbooks, tablets, other
                phones, USB modems, network routers, media players, gaming
                consoles, or other data-capable devices) by tethering devices
                together, by SIM card transfer, or any other means.
              </Pcol>

              <Hcol>Performance Characteristics</Hcol>
              <Pcol>
                EIN offers many mass market broadband Internet access service
                options, each of which may have a different service capability
                speed. The term speed is commonly used as a shorthand way to
                describe the capacity at which a particular mass market
                broadband Internet access service can transmit data. This
                capacity is typically measured in the number of kilobits,
                megabits or gigabits that can be transmitted in one second
                (Kbps, Mbps or Gbps). Some applications, like a short email
                without attachments or basic web browsing, do not require high
                service capability speeds to function optimally. Other
                activities, like transferring large data files, can be performed
                faster with higher-speed services. Your service capability speed
                may not be suitable for some applications, particularly those
                involving real-time or near real-time, high-bandwidth uses such
                as streaming video or video conferencing.
              </Pcol>
              <Pcol>
                Because service performance varies on an end-to-end basis, EIN’s
                service capability speeds are limited to, and measured between,
                your location and a point on EIN’s network, which constitutes
                only one segment of the end to end transmission path connecting
                your location to Internet websites or content providers.
                End-to-end performance of your service depends on a variety of
                factors, including: the number of subscribers simultaneously
                using the network; customer location; destination and traffic on
                the Internet; Wi-Fi connectivity; the capabilities and
                performance of your Local Area Network (LAN); interference with
                high frequency spectrum on your telephone line; wiring inside
                your premises, office or apartment; the capacity or performance
                of your devices or modem; the server with which you are
                communicating; internal network management factors (including
                overhead, which refers to the various control and signaling data
                required to achieve the reliable transmission of Internet access
                data); and the networks you and others are using when
                communicating.
              </Pcol>
              <Pcol>
                EIN offers a wide variety of services to its customers
                (including Voice over Internet Protocol (VoIP), Internet
                Protocol (IP)-video, unified messaging, Voice over LTE (VoLTE),
                enterprise networking services, and other services), which share
                EIN’s network infrastructure and may affect the availability of
                network resources for broadband Internet access services. Your
                use of these services may affect the performance of your mass
                market broadband Internet access service. These services may
                rely on particular network practices to assign different levels
                of priority dynamically or statically. EIN does not currently
                have the capability to make any such functionality available to
                edge providers. In addition, although EIN engineers its network
                to accommodate all users and user types based on a variety of
                factors, including average and anticipated peak usage of the
                network, many factors cannot be anticipated or are outside of
                EIN’s control. These factors can impact the availability of
                network resources for mass market broadband Internet access
                services at any particular time. Consequently, EIN does not
                guarantee the performance of your service on an end-to-end
                basis.
              </Pcol>
              <Pcol>
                Other factors that are relevant to specific services include,
                but are not limited to, the following: Wired Services. Service
                performance may be affected by the wiring inside your premises,
                the distance between your premises and an EIN central office,
                the capabilities of your computer, and the applications you use.
                In addition, to provide our U-verse TV customers with a
                consistently high-quality video service, the speed of EIN
                broadband Internet access service may be reduced when a customer
                is using his or her U-verse TV service in a manner that requires
                high bandwidth. Please click here for our High-Speed Internet
                Access Terms of Service.
              </Pcol>
              <Pcol>
                Because many different factors can affect the performance of
                your mass market broadband Internet access service, EIN does not
                guarantee specific performance levels (such as of speed or
                latency or packet loss) for our mass market broadband Internet
                access services. We strive to manage our network to provide you
                optimal performance. The performance you can expect to receive
                from the mass market broadband Internet access services we offer
                is described below. Wired Service.
              </Pcol>
              <Pcol>
                EIN offers mass market wired broadband Internet access services
                in discrete speed tiers. Our mass market wired broadband
                Internet access customers should expect to see service
                capability speeds within the speed tier of their service plan.
                For example, a customer with EIN’s High Speed Internet Starter
                Service should expect service capability download speeds between
                3.1 and 6.0 Mbps.
              </Pcol>

              <Hcol>Latency</Hcol>
              <Pcol>
                Latency, also known as delay, is the amount of time from when a
                data packet is sent to when it is received. For mass market
                broadband Internet access services, latency is usually expressed
                as the round-trip time in milliseconds (ms) that it takes for a
                data packet to travel between two end points on the Internet
                (from point A to point B and then back to point A). Some
                applications, such as email, can tolerate a substantial amount
                of latency without any noticeable impact on the application’s
                performance. Other applications, such as real-time video
                conferencing, require lower latency to function properly.
                End-to-end latency reflects the cumulative effect of the
                individual latencies that occur along the end-to-end network
                path. Although latencies can vary due to several factors,
                including some beyond EIN’s control, our mass market broadband
                Internet access service customers can typically expect the
                following round-trip latencies when accessing the Internet:
              </Pcol>
              <Hcol>Packet Loss</Hcol>
              <Pcol>
                Packet loss occurs when one or more packets of data traveling
                across the network fail to reach their destination, typically
                due to network congestion, and is measured as a percentage of
                packets lost with respect to packets sent. A small percentage of
                packet loss is inevitable, and indeed desirable, in Internet
                protocol networks and applications. These networks and
                applications have been designed to tolerate dropped packets and
                rely on packet retransmission to replace such packets to
                maintain high speed transmission of data across the Internet.
                Without some packet loss, network (and thus application)
                performance could degrade significantly.
              </Pcol>
              <Hcol>Terms of Service</Hcol>
              <Pcol>
                At EIN, we take our customers’ privacy very seriously. We have a
                comprehensive Privacy Policy that applies to all uses of EIN
                products and services. This Privacy Policy identifies and
                describes the way EIN uses and protects the information we
                collect about customers and users. You can view the EIN Privacy
                Policy at{" "}
                <Link href="/privacy-policy">
                  https://easyinternetnow.com/privacy-policy
                </Link>
                .
              </Pcol>
              <Pcol>
                Where can I get assistance if I have a concern or need more
                information about my EIN broadband Internet access service?
              </Pcol>
              <Pcol>
                If you have questions or concerns about your mass market EIN
                broadband Internet access service, please contact us at
                <Link href="/support">https://easyinternetnow.com/support</Link>
                .
              </Pcol>
              <Pcol>
                © 2017 EIN Intellectual Property. All rights reserved.
              </Pcol>
            </div>
          </div>

          <FaqSidebar />
        </div>
      </div>
    </section>
  </Layout>
);

export default BroadbandInformation;
